/*
 * The doc-body stylesheet, injected into the TechDocs shadow root.
 *
 * TechDocs renders mkdocs-material's own HTML inside a shadow DOM and styles it
 * from `@backstage/plugin-techdocs`'s style transformer, which writes a <style>
 * into the shadow document's <head>. Nothing in `hone-portal.css` reaches
 * across that boundary, so the reader's prose grammar lives here as a string
 * and is appended after the stock rules by `ReaderStylesAddon`.
 *
 * Kept deliberately separate from the Backstage reader chrome: this file only
 * ever describes mkdocs-material classes (`.md-*`, `.admonition`, pygments
 * tokens). Chrome — the header bar, sidebar, page frame — stays in
 * `hone-portal.css`.
 *
 * The light-mode values below are the `1f` mock's, spelled as literal hexes.
 * Inherited CSS custom properties (var(--helix-*), var(--hone-portal-*)) do
 * reach here — they cross the shadow boundary through the shadow host — so they
 * can be used; only a `:root {}` rule placed inside the shadow matches nothing.
 * The dark section at the end of this file prefers those tokens.
 */
export const readerStyles = `
/*==================  Zones  ==================*/

/*
 * The shadow root holds a cloned html/body, and mkdocs makes them scrollable.
 * That turns the shadow html into the nearest scrollport, so the sticky rails
 * below stick to a box that never moves — they scroll away with the prose.
 * Handing scrolling back to the layout column fixes it, and lets the reader use
 * the portal canvas instead of mkdocs' own grey.
 */
html,
body {
  height: auto !important;
  overflow: visible !important;
  background: #fff;
  /*
   * mkdocs builds its font stacks as var(--md-text-font, _) — the sentinel is
   * the literal underscore that shows up in the computed value when the token
   * is unset, which it is here: material declares it on :root, and :root
   * matches nothing inside a shadow tree. Naming the two fonts on the cloned
   * html carries them to the whole reader — doc nav, ToC, skip link — rather
   * than the prose alone.
   */
  --md-text-font: 'DM Sans';
  --md-code-font: 'DM Mono';
}

/*
 * Three zones: 248px doc nav, a centred 680px prose measure, 224px "On this
 * page". mkdocs' inner row is already a flex container, but the stock TechDocs
 * rules take both sidebars out of it — fixed-position, 16rem wide, with a
 * matching margin on the content — and then drive their top and height from
 * scroll handlers. Putting them back in the row as sticky columns is both
 * closer to the design and self-maintaining; !important is what beats the
 * inline top/height those handlers write.
 */
.md-sidebar {
  position: sticky !important;
  top: 0 !important;
  align-self: flex-start;
  box-sizing: border-box;
  /* 281px of column for 248px of content once the 20/12 gutters are removed;
   * at the old 248 the section labels wrapped to two lines. */
  width: 281px;
  height: auto !important;
  /* The reader scrolls inside the layout column, which the portal header strip
   * sits above. The 53px (bar plus its rule) is spelled as a literal here and
   * has to track the portal header height. */
  max-height: calc(100vh - 53px);
  /* Stock CSS zeroes the rails' bottom padding at desktop widths, with
   * !important. */
  padding: 24px 12px 24px 20px !important;
  overflow-x: hidden;
  overflow-y: auto;
  opacity: 1 !important;
  /* A long nav still scrolls; the bar itself would draw a grey gutter beside
   * every rail, and mkdocs hides its own for the same reason. */
  scrollbar-width: none;
}

.md-sidebar::-webkit-scrollbar {
  display: none;
}

/* Stock CSS pads the inner column to reserve a scrollbar gutter it no longer
 * needs, which pushes the rails off their 12px inset. */
.md-sidebar .md-sidebar__inner {
  padding-right: 0;
}

.md-sidebar .md-sidebar__scrollwrap {
  width: 100%;
  height: auto;
  margin: 0;
  overflow: visible;
}

.md-sidebar--primary {
  border-right: 1px solid #f0f0ef;
  display: flex;
  flex-direction: column;
  overflow-anchor: none;
}

/* The addon container is prepended to the rail; the current doc set's pages
 * come first and "All documentation" follows. */
.md-sidebar--primary > [data-techdocs-addons-location='primary sidebar'] {
  order: 1;
}

/* A column flex container shrinks its children to the rail's max-height, which
 * stacks the doc-set title over its first link; the rail scrolls instead. */
.md-sidebar--primary > * {
  flex-shrink: 0;
}

/* The doc-set title is sticky in the rail's scroller; with the long
 * "All documentation" tree below it, a scrolled rail pinned it over the first
 * link. It scrolls with the list instead. */
.md-sidebar--primary .md-nav--primary > .md-nav__title {
  position: static;
}

.md-sidebar--secondary {
  width: 248px;
  padding: 40px 24px 24px 0 !important;
  border-right: none;
}

.md-content {
  flex: 1;
  min-width: 0;
  max-width: none;
  margin: 0;
  /* The mock gutters the prose column; without it the measure can sit flush
   * against the ToC rail. */
  padding: 0 48px;
}

/*
 * 680px is a maximum, not a width: below ~1450px the column between the two
 * rails is narrower than that, and a fixed width overflows it and paints the
 * article under the sticky ToC.
 */
.md-content__inner {
  width: 100%;
  max-width: 680px;
  /* mkdocs' own 0 .8rem side margin wins the tie and shifts the measure 19px
   * right of the gutter, leaving the footer below it out of line. */
  margin: 0 auto !important;
  padding: 40px 0 48px;
}

/* mkdocs prints a zero-height ::before spacer above the first heading. */
.md-content__inner::before {
  display: none;
}

/*==================  Doc nav  ==================*/

/*
 * The nav opens with the doc set's name, as the mock's does. mkdocs styles this
 * for its own mobile drawer — a full-height bar with a shadow and a logo
 * button — so the bar is undone and only the label kept.
 */
.md-nav--primary > .md-nav__title {
  display: block;
  height: auto;
  margin: 0;
  padding: 0 12px 10px;
  background: none;
  box-shadow: none;
  cursor: default;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  color: #202020;
  white-space: normal;
}

.md-nav--primary > .md-nav__title .md-nav__button,
.md-nav--primary > .md-nav__title .md-logo {
  display: none;
}

.md-nav {
  font-size: 12.5px;
}

.md-nav__list {
  padding: 0;
}

.md-nav__item {
  padding: 0;
}

.md-nav__link {
  position: relative;
  display: flex;
  align-items: center;
  /* Row rhythm is 32px; a long page title wraps and grows the row rather than
   * spilling out of it. */
  min-height: 32px;
  margin: 2px 0 0;
  padding: 5px 8px 5px 12px;
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.3;
  color: #555555;
  text-decoration: none;
}

.md-nav__link:hover {
  background: #f8f8f8;
  color: #202020;
}

/* Nested pages step in by 14px per level, matching the mock's two-level tree. */
.md-nav__item .md-nav .md-nav__link {
  padding-left: 26px;
}

.md-nav__item .md-nav .md-nav .md-nav__link {
  padding-left: 40px;
}

/*
 * Active page: inset pill, ink label, 3px yellow rail. The stock layout rule
 * underlines the active link and paints it the link colour; both are replaced.
 */
/*
 * Only the current page takes the pill. mkdocs also marks every ancestor
 * section as active, which would light up the section label and its leaf at
 * once; the section just stays expanded. It still has to shed the stock
 * underline that comes with the active class.
 */
.md-nav__item--active > .md-nav__link {
  text-decoration: none;
}

.md-nav__item--active:not(.md-nav__item--nested) > .md-nav__link,
.md-nav .md-nav__link--active {
  background: #f6f6f5;
  font-weight: 600;
  color: #202020;
  text-decoration: none;
}

.md-nav__item--active:not(.md-nav__item--nested) > .md-nav__link::before,
.md-nav .md-nav__link--active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 2px;
  background: #f8f93f;
}

/* Section labels (a nav item that owns a subtree) read as headings, not rows. */
.md-nav__item--nested > .md-nav__link {
  font-size: 12px;
  font-weight: 600;
  color: #202020;
}

/*==================  Prose  ==================*/

.md-typeset {
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  line-height: 1.75;
  color: #363636;
}

.md-typeset p {
  margin: 0 0 18px;
  text-wrap: pretty;
}

.md-typeset h1 {
  /* The mock separates the title from the body with an eyebrow meta line; the
   * reader has no per-page metadata to put there, so the gap carries it. */
  margin: 0 0 24px;
  font-size: 29px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.025em;
  color: #202020;
}

.md-typeset h2 {
  margin: 26px 0 12px;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.015em;
  color: #202020;
}

.md-typeset h3 {
  margin: 26px 0 10px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: -0.01em;
  color: #202020;
}

.md-typeset h4,
.md-typeset h5,
.md-typeset h6 {
  margin: 22px 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: #202020;
}

/* Heading anchor: mkdocs' ¶ permalink, restyled to the mock's grey #. */
.md-typeset .headerlink {
  margin-left: 8px;
  font-size: 15px;
  font-weight: 400;
  color: #c9c9c7;
  opacity: 0;
  transition: opacity 120ms ease;
}

.md-typeset h1:hover .headerlink,
.md-typeset h2:hover .headerlink,
.md-typeset h3:hover .headerlink,
.md-typeset .headerlink:focus {
  opacity: 1;
}

/*
 * mkdocs floats an "edit this page" link above the article as a bare pencil,
 * which readers kept missing. It is a labelled button instead: a bordered chip
 * with the pencil and "Edit this page".
 */
.md-typeset .md-content__button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0 0 12px;
  padding: 5px 11px 5px 9px;
  border: 1px solid #d9d9d6;
  border-radius: 8px;
  background: #f6f6f5;
  color: #202020;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.2;
  opacity: 1;
}

.md-typeset .md-content__button::after {
  content: 'Edit this page';
}

.md-typeset .md-content__button:hover {
  background: #ececea;
  border-color: #c4c4c0;
  color: #202020;
}

.md-typeset .md-content__button svg {
  width: 14px;
  height: 14px;
  fill: currentColor;
}

/* The drawer toggle is not offered at any width. */
.md-typeset .md-content__button[for='__drawer'],
.md-typeset #toggle-sidebar {
  display: none;
}

.md-typeset a {
  color: #039cfb;
  text-decoration: none;
}

.md-typeset a:hover {
  color: #027ac4;
}

.md-typeset ul,
.md-typeset ol {
  margin: 0 0 18px;
  padding-left: 22px;
}

/* Stock uses a 0.5em list gap on a two-selector rule; match its weight. */
.md-typeset ul li,
.md-typeset ol li {
  margin-bottom: 6px;
}

.md-typeset li > ul,
.md-typeset li > ol {
  margin: 6px 0 0;
}

.md-typeset blockquote {
  margin: 0 0 26px;
  padding: 2px 0 2px 18px;
  /* Stock sets the quote rule from a translucent fg token, separately from the
   * shorthand. */
  border-left: 3px solid #ebebeb;
  border-left-color: #ebebeb !important;
  color: #6d6d6d;
}

.md-typeset hr {
  margin: 26px 0;
  border: none;
  border-top: 1px solid #f0f0ef;
}

.md-typeset img {
  max-width: 100%;
  border-radius: 10px;
}

/*
 * A rounded card, a header label over a hairline, and one hairline per row —
 * no grey header bar; the weight of the label carries it.
 *
 * The :not([class]) is mkdocs' own selector for markdown tables, and matching
 * it is what makes these rules land: at a plain .md-typeset table the stock
 * rules out-specify every property they also set — the row rules were being
 * dropped for stock's darker rgba(0,0,0,.15) hairline on both edges of every
 * cell. mkdocs classes only the code-block table, which has its own rules
 * below, so nothing is left unstyled by the narrower selector.
 */
.md-typeset table:not([class]) {
  margin: 0 0 26px;
  border: none;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06), 0 2px 4px rgba(0, 0, 0, 0.04);
  font-size: 13px;
  overflow: hidden;
}

.md-typeset table:not([class]) th {
  padding: 10px 14px;
  background: none;
  border: none;
  border-bottom: 1px solid #f0f0ef;
  font-size: 13.5px;
  font-weight: 600;
  color: #202020;
}

.md-typeset table:not([class]) td {
  padding: 10px 14px;
  border: none;
  border-top: 1px solid #f7f7f6;
  color: #363636;
}

/* Inline code: inset chip, no ring — the surrounding text keeps its colour. */
.md-typeset code {
  padding: 1px 5px;
  border-radius: 4px;
  background: #f1f0ef;
  box-shadow: none;
  font-family: 'DM Mono', monospace;
  font-size: 12.5px;
  color: inherit;
}

/*==================  Callouts  ==================*/

/*
 * Five types, one shape: 6% tint fill, an inset hairline in the type's hue, and
 * the type's black icon in a 16px column. mkdocs-material emits
 * an admonition div whose first child is a p.admonition-title, so the icon
 * column is the title's own ::before mask and the body is indented to line up
 * under the title rather than laid out as a flex row.
 *
 * Tip is the only pale-yellow surface in the system.
 */
.md-typeset .admonition,
.md-typeset details {
  display: block;
  margin: 0 0 26px;
  padding: 14px 16px;
  border: none;
  border-radius: 10px;
  background: #f8f8f8;
  box-shadow: inset 0 0 0 1px #e8e8e6;
  /* Some repos ship a docs stylesheet that pins the admonition font size with
   * !important; the portal's callout scale has to hold across all of them. */
  font-size: 13.5px !important;
  line-height: 1.6;
  color: #363636;
  /* The ring is a static token, not a state; mkdocs fades box-shadow in. */
  transition: none;
}

.md-typeset .admonition > :not(.admonition-title),
.md-typeset details > :not(summary) {
  margin-left: 28px;
}

/* Without this the callout's bottom padding reads ~10px heavier than its top. */
.md-typeset .admonition > :last-child,
.md-typeset details > :last-child {
  margin-bottom: 0 !important;
}

/*
 * mkdocs-material paints the title strip and the icon per type, at three
 * classes of specificity (.md-typeset .warning > .admonition-title), so the
 * shared shape has to assert itself rather than out-specify each type.
 */
.md-typeset .admonition-title,
.md-typeset summary {
  position: relative;
  margin: 0 0 3px !important;
  padding: 0 0 0 28px !important;
  border: none !important;
  background: none !important;
  /* Both are forced: mkdocs' own title rule and any docs stylesheet a repo
   * ships reach the same declarations, and a taller line box drops the label
   * away from the icon beside it. */
  font-size: 12.5px !important;
  font-weight: 600;
  line-height: 1.5 !important;
  color: #202020;
}

.md-typeset .admonition-title::before,
.md-typeset summary::before {
  /* Centred on the title's line box rather than offset from its top: the icon
   * is a fixed 16px against a line box the cascade can still grow, and a
   * literal top leaves the glyph riding above the label. */
  top: 50% !important;
  transform: translateY(-50%);
  left: 0 !important;
  width: 16px !important;
  height: 16px !important;
  background-color: #202020 !important;
  /* The mask is authored at mkdocs' 20px icon box; scale it to the 16px one. */
  -webkit-mask-size: contain;
  mask-size: contain;
}

/* mkdocs draws a caret on collapsible admonitions; the tint carries the type. */
.md-typeset summary::after {
  top: 4px;
  color: #9a9a9a;
}

/* Note / abstract / example / quote — the neutral default above. */

.md-typeset .admonition.info,
.md-typeset .admonition.todo,
.md-typeset .admonition.question,
.md-typeset .admonition.help,
.md-typeset .admonition.faq,
.md-typeset details.info,
.md-typeset details.question {
  background: rgba(3, 156, 251, 0.06);
  box-shadow: inset 0 0 0 1px rgba(3, 156, 251, 0.3);
}

.md-typeset .admonition.info > .admonition-title,
.md-typeset .admonition.todo > .admonition-title,
.md-typeset .admonition.question > .admonition-title,
.md-typeset .admonition.help > .admonition-title,
.md-typeset .admonition.faq > .admonition-title,
.md-typeset details.info > summary,
.md-typeset details.question > summary {
  color: #027ac4;
}

.md-typeset .admonition.tip,
.md-typeset .admonition.hint,
.md-typeset .admonition.important,
.md-typeset details.tip {
  background: #fefeec;
  box-shadow: inset 0 0 0 1px #eceb9e;
}

.md-typeset .admonition.warning,
.md-typeset .admonition.caution,
.md-typeset .admonition.attention,
.md-typeset details.warning {
  background: rgba(194, 97, 10, 0.06);
  box-shadow: inset 0 0 0 1px rgba(194, 97, 10, 0.3);
}

.md-typeset .admonition.warning > .admonition-title,
.md-typeset .admonition.caution > .admonition-title,
.md-typeset .admonition.attention > .admonition-title,
.md-typeset details.warning > summary {
  color: #b45309;
}

.md-typeset .admonition.success,
.md-typeset .admonition.check,
.md-typeset .admonition.done,
.md-typeset details.success {
  background: rgba(24, 154, 92, 0.06);
  box-shadow: inset 0 0 0 1px rgba(24, 154, 92, 0.3);
}

.md-typeset .admonition.success > .admonition-title,
.md-typeset .admonition.check > .admonition-title,
.md-typeset .admonition.done > .admonition-title,
.md-typeset details.success > summary {
  color: #157347;
}

/* danger/failure/bug carry the error hue on the same shape as the other five. */
.md-typeset .admonition.danger,
.md-typeset .admonition.error,
.md-typeset .admonition.failure,
.md-typeset .admonition.fail,
.md-typeset .admonition.missing,
.md-typeset .admonition.bug,
.md-typeset details.danger,
.md-typeset details.bug {
  background: rgba(217, 48, 54, 0.06);
  box-shadow: inset 0 0 0 1px rgba(217, 48, 54, 0.3);
}

.md-typeset .admonition.danger > .admonition-title,
.md-typeset .admonition.error > .admonition-title,
.md-typeset .admonition.failure > .admonition-title,
.md-typeset .admonition.fail > .admonition-title,
.md-typeset .admonition.missing > .admonition-title,
.md-typeset .admonition.bug > .admonition-title,
.md-typeset details.danger > summary,
.md-typeset details.bug > summary {
  color: #d93036;
}

/*==================  Code blocks  ==================*/

/*
 * Code sits on ink, wrapped in a rounded surface with a mono header bar
 * carrying the language and a copy affordance. The bar and button are added by
 * CodeBlockAddon; everything visual is here.
 */
.md-typeset .highlight,
.md-typeset pre {
  margin: 0 0 26px;
  border-radius: 10px;
  background: #202020;
  overflow: hidden;
}

/* The inner <pre> keeps a block's bottom margin, which shows up as ~37px of
 * dead ink below the last line. */
.md-typeset .highlight pre,
.md-typeset .highlight > pre,
.md-typeset pre > code {
  margin: 0 !important;
  border-radius: 0;
  background: #202020;
}

/*
 * DM Mono is named again on the block itself, so a docs stylesheet that resets
 * the code stack cannot take it back. The <pre> also has to own the horizontal
 * scroll — stock hides its overflow, which silently clips long lines inside a
 * surface that looks complete.
 */
.md-typeset pre,
.md-typeset .highlight {
  font-family: 'DM Mono', monospace;
}

.md-typeset .highlight pre,
.md-typeset .highlight td.code,
.md-typeset pre > code {
  overflow-x: auto;
  /* The system scrollbar is drawn for a light surface and reads as a grey slab
   * across the bottom of the ink block. */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.md-typeset .highlight pre::-webkit-scrollbar,
.md-typeset .highlight td.code::-webkit-scrollbar,
.md-typeset pre > code::-webkit-scrollbar {
  height: 8px;
}

.md-typeset .highlight pre::-webkit-scrollbar-track,
.md-typeset .highlight td.code::-webkit-scrollbar-track,
.md-typeset pre > code::-webkit-scrollbar-track {
  background: transparent;
}

.md-typeset .highlight pre::-webkit-scrollbar-thumb,
.md-typeset .highlight td.code::-webkit-scrollbar-thumb,
.md-typeset pre > code::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.18);
}

.md-typeset .highlight pre:hover::-webkit-scrollbar-thumb,
.md-typeset .highlight td.code:hover::-webkit-scrollbar-thumb,
.md-typeset pre > code:hover::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
}

.md-typeset pre > code {
  display: block;
  padding: 16px 18px;
  box-shadow: none;
  font-family: 'DM Mono', monospace;
  font-size: 12.5px;
  line-height: 1.8;
  color: #f4f4f4;
  overflow-x: auto;
}

/*
 * Line-numbered blocks nest a highlighttable inside the surface, with the
 * gutter and the code as table cells. Strip the table chrome so the two columns
 * read as one block, and keep the inner .highlight from repeating the surface.
 */
.md-typeset .highlight .highlight {
  margin: 0;
  border-radius: 0;
}

.md-typeset .highlighttable {
  display: block;
  width: 100%;
  margin: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  font-size: inherit;
  overflow: hidden;
}

.md-typeset .highlighttable tbody,
.md-typeset .highlighttable tr {
  display: flex;
  width: 100%;
}

.md-typeset .highlighttable td {
  padding: 0;
  border: none;
  background: transparent;
}

.md-typeset .highlighttable td.code {
  flex: 1;
  min-width: 0;
}

/* Stock draws an inset rule down the gutter's right edge; the design runs the
 * numbers and the code on one uninterrupted ink surface. */
.md-typeset .highlighttable .linenos,
.md-typeset .highlighttable .linenodiv {
  box-shadow: none;
  border-radius: 0;
}

.md-typeset .highlighttable .linenos {
  flex: none;
  padding: 16px 0 16px 14px;
  font-family: 'DM Mono', monospace;
  font-size: 12.5px;
  line-height: 1.8;
  color: #6d6d6d;
  user-select: none;
}

.md-typeset .highlighttable .linenos pre,
.md-typeset .highlighttable .linenos .linenodiv pre {
  margin: 0;
  padding: 0;
  border-radius: 0;
  background: transparent;
  /* The gutter has to share the code's rhythm exactly or the numbers drift a
   * few pixels further from their line with every row. */
  font-family: 'DM Mono', monospace;
  font-size: 12.5px;
  line-height: 1.8;
}

/* The code cell's own <pre> otherwise inherits the 15px/1.4 prose rhythm; the
 * numbers opposite it run at 12.5px/1.8, so a stray text node or the leading
 * empty span in that <pre> could open a taller line box and shear the two
 * columns apart. Pin it to the same rhythm as the gutter and the <code>. */
.md-typeset .highlighttable td.code pre {
  font-size: 12.5px;
  line-height: 1.8;
}

/* mkdocs' own copy button (content.code.copy) would double up with ours. */
.md-typeset .md-clipboard {
  display: none;
}

.hone-code-bar {
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  color: #9a9a9a;
}

.hone-code-bar__spacer {
  flex: 1;
}

.hone-code-copy {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: none;
  background: none;
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  color: #c9c9c7;
  cursor: pointer;
}

.hone-code-copy:hover {
  color: #f4f4f4;
}

/* The glyph is authored in currentColor on a light surface; invert it for ink. */
.hone-code-copy__icon {
  width: 12px;
  height: 12px;
  filter: invert(1);
  opacity: 0.7;
}

.hone-code-copy:focus-visible {
  outline: none;
  border-radius: 4px;
  box-shadow: 0 0 0 2px #202020, 0 0 0 4px #039cfb;
}

/*
 * Pygments token colours, repainted for the ink surface.
 *
 * The reader injects mkdocs-material's own \`--md-code-hl-*\` palette onto
 * \`:host\`, but derives it from the portal's light MUI theme, so every upstream
 * token colour resolves to a near-black, light-surface value. On this ink block
 * any class left to that palette vanishes — and because pygments tags plain
 * identifiers \`.n\`/\`.nx\` (the bulk of most languages) those went invisible,
 * which is why C# read as unhighlighted while XML, being all tags and strings,
 * did not. So the whole token set is coloured here, at a specificity
 * (\`.md-typeset .highlight .x\`) that beats the injected rules: no class is left
 * to fall back to the dead palette.
 */

/* Identifiers carry the base ink foreground; without this the bulk of C#,
 * Python, SQL, Kotlin, Ruby, C/C++ and friends disappears. \`.err\` rides along
 * so a stray un-lexable token stays legible rather than black-on-ink. */
.md-typeset .highlight .n,
.md-typeset .highlight .nx,
.md-typeset .highlight .err {
  color: #f4f4f4;
}

/* Comments, including the hashbang and C-style preprocessor lines. */
.md-typeset .highlight .c,
.md-typeset .highlight .c1,
.md-typeset .highlight .ch,
.md-typeset .highlight .cm,
.md-typeset .highlight .cp,
.md-typeset .highlight .cpf,
.md-typeset .highlight .cs {
  color: #7a7a7a;
  font-style: italic;
}

/* Keywords, including constants (true / false / null) and pseudo-keywords. */
.md-typeset .highlight .k,
.md-typeset .highlight .kc,
.md-typeset .highlight .kd,
.md-typeset .highlight .kn,
.md-typeset .highlight .kp,
.md-typeset .highlight .kr,
.md-typeset .highlight .kt,
.md-typeset .highlight .ow {
  color: #d0a2f7;
}

/* Strings, char / regex / symbol literals and their delimiters. */
.md-typeset .highlight .s,
.md-typeset .highlight .l,
.md-typeset .highlight .s1,
.md-typeset .highlight .s2,
.md-typeset .highlight .sa,
.md-typeset .highlight .sb,
.md-typeset .highlight .sc,
.md-typeset .highlight .sd,
.md-typeset .highlight .se,
.md-typeset .highlight .sh,
.md-typeset .highlight .si,
.md-typeset .highlight .sr,
.md-typeset .highlight .ss,
.md-typeset .highlight .sx,
.md-typeset .highlight .dl {
  color: #6fd39b;
}

.md-typeset .highlight .m,
.md-typeset .highlight .mb,
.md-typeset .highlight .mf,
.md-typeset .highlight .mh,
.md-typeset .highlight .mi,
.md-typeset .highlight .mo,
.md-typeset .highlight .il {
  color: #f8f93f;
}

/* Function, class, decorator and exception names. */
.md-typeset .highlight .nc,
.md-typeset .highlight .nd,
.md-typeset .highlight .ne,
.md-typeset .highlight .nf,
.md-typeset .highlight .fm {
  color: #8ecdfb;
}

/* Builtins, namespaces, tags, attributes and variables. */
.md-typeset .highlight .bp,
.md-typeset .highlight .na,
.md-typeset .highlight .nb,
.md-typeset .highlight .ni,
.md-typeset .highlight .nl,
.md-typeset .highlight .nn,
.md-typeset .highlight .no,
.md-typeset .highlight .nt,
.md-typeset .highlight .nv,
.md-typeset .highlight .py,
.md-typeset .highlight .vc,
.md-typeset .highlight .vg,
.md-typeset .highlight .vi,
.md-typeset .highlight .vm {
  color: #c9c9c7;
}

.md-typeset .highlight .o,
.md-typeset .highlight .p,
.md-typeset .highlight .gp {
  color: #9a9a9a;
}

/* Generic tokens: diff insert/delete, console output and error / traceback. */
.md-typeset .highlight .gd {
  color: #f4a3a6;
}

.md-typeset .highlight .gi {
  color: #6fd39b;
}

.md-typeset .highlight .gr,
.md-typeset .highlight .gt {
  color: #f4a3a6;
}

.md-typeset .highlight .gh,
.md-typeset .highlight .gu,
.md-typeset .highlight .go {
  color: #c9c9c7;
}

.md-typeset .highlight .ge {
  font-style: italic;
}

.md-typeset .highlight .gs {
  font-weight: 600;
}

/* Line numbers and highlighted lines both assume a light surface upstream. */
.md-typeset .highlighttable .linenos,
.md-typeset .highlight .linenos,
.md-typeset .highlight .lineno {
  background: #202020;
  color: #6d6d6d;
}

.md-typeset .highlight .hll {
  background: rgba(248, 249, 63, 0.12);
}

/*==================  Footer (previous / next)  ==================*/

/*
 * The footer spans the full reader width, below all three columns, so its
 * measure has to be centred against the prose column rather than the whole
 * row — hence the two rails' widths as padding.
 */
/*
 * The footer spans the full reader width, below all three columns, so its
 * measure has to be re-derived: the rails as padding, then the prose column's
 * own 48px gutters and centring on the inner row. TechDocs also writes a pixel
 * width onto the footer from a scroll handler, which is a scrollbar's width off
 * from the column above it — hence the width reset.
 */
.md-footer {
  position: static;
  box-sizing: border-box;
  width: auto !important;
  margin: 0;
  padding: 0 248px 0 281px;
  background: none;
  box-shadow: none;
}

.md-footer__inner {
  display: flex;
  box-sizing: border-box;
  justify-content: space-between;
  width: 100%;
  max-width: 776px;
  margin: 0 auto;
  padding: 0 48px 48px;
}

/*
 * Stock gives each link a fixed 16rem and lets it shrink; in a 680px row that
 * collapses both to a few characters ("Pr:" / "H."). Each side takes half the
 * row and truncates its own title instead.
 */
.md-footer__link {
  display: flex;
  flex: 0 1 48%;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  width: auto;
  max-width: 48%;
  margin: 0;
  padding: 0;
  opacity: 1;
}

.md-footer__link--next {
  text-align: right;
}

.md-footer__direction {
  margin: 0;
  font-size: 11px;
  font-weight: 400;
  color: #9a9a9a;
  white-space: nowrap;
  opacity: 1;
}

/* mkdocs hides the arrows in its icon buttons; the design carries them in the
 * label instead. */
.md-footer__link--prev .md-footer__direction::before {
  content: '← ';
}

.md-footer__link--next .md-footer__direction::after {
  content: ' →';
}

/* The prev block's title is right-aligned inside its own box by default, which
 * pushes it off the measure's left edge. */
.md-footer__link--prev {
  align-items: flex-start;
  text-align: left;
}

.md-footer__title {
  margin: 0;
  padding: 0;
  background: none;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  color: #039cfb;
}

.md-footer__title .md-ellipsis {
  display: block;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The arrows are part of the direction label in the design, not glyph buttons. */
.md-footer__button {
  display: none;
}

/* Copyright / "Made with Material" strip has no place in the portal reader. */
.md-footer-meta {
  display: none;
}

/*==================  Secondary sidebar  ==================*/

/* mkdocs' own table of contents is replaced by the scroll-spy addon. */
.md-sidebar--secondary .md-nav--secondary {
  display: none;
}

/*==================  Phone  ==================*/

/*
 * 599.95px is \`theme.breakpoints.down('xs')\` — the width at which the portal
 * shell swaps its desktop chrome for the mobile top bar and bottom tab bar. The
 * reader collapses to the mock's single column there: no doc nav, no "On this
 * page" rail (\`DocsNavAddon\` carries both as a split pill and the sheet it
 * opens, reading the hidden nav's own nodes), and the prose runs the full width
 * inside 24px gutters. Everything below the measure — prose scale, callouts,
 * code blocks — is untouched.
 */
@media (max-width: 599.95px) {
  .md-sidebar,
  .md-sidebar--primary,
  .md-sidebar--secondary {
    display: none !important;
  }

  .md-content {
    padding: 0 24px;
  }

  /*
   * mkdocs-material draws its drawer toggle beside the title below its own
   * mobile breakpoint — a \`label[for="__drawer"]\` that opens the primary
   * sidebar. The sidebar is hidden above, so the glyph is a control that does
   * nothing, sitting in the title's line beside the feedback link.
   */
  .md-content__button[for='__drawer'] {
    display: none;
  }

  .md-content__inner {
    max-width: none;
    padding: 24px 0 32px;
  }

  /* The heading scale steps down with the measure; 29px wraps a real doc title
   * to three lines at 402px. */
  .md-typeset h1 {
    margin: 0 0 18px;
    font-size: 24px;
    letter-spacing: -0.02em;
  }

  .md-typeset h2 {
    font-size: 17px;
  }

  /*
   * The footer's padding is the two rails' widths, and with the rails gone it
   * pushes both links off the screen instead of clearing them.
   */
  .md-footer {
    padding: 0;
  }

  /*
   * The shell reserves the tab bar's height under the page; the pill floats
   * 12px above that, so the last thing on the page needs a further 52px of
   * clearance or the pill covers it.
   */
  .md-footer__inner {
    max-width: none;
    padding: 0 24px 52px;
  }
}

/*==================  Dark mode  ==================*/

/*
 * Strategy B: one appended section that restates only the colour-bearing
 * declarations for dark. \`data-theme-mode='dark'\` is set on the OUTER
 * document.body, never inside this shadow root, so a bare
 * [data-theme-mode='dark'] selector here would match nothing.
 * ReaderStylesAddon mirrors the attribute onto the shadow host, and these rules
 * use :host([data-theme-mode='dark']), which works in all browsers.
 *
 * Values prefer var(--helix-*) tokens — inherited custom properties cross the
 * shadow boundary and already carry the active mode's value. Literal colours
 * appear only for the dark surface tiers Helix has no token for yet; each is
 * flagged to reconcile when Helix ships its dark tokens.
 *
 * Geometry, spacing and font sizes are not restated — only colour, background,
 * border and shadow. The syntax-highlight code block (.highlight / pre) is
 * already an ink surface with a full light-on-dark pygments palette and sits a
 * step below the reader canvas, so it is left mode-agnostic.
 */

/* Reader canvas — a dark tier Helix has no token for yet; reconcile. */
:host([data-theme-mode='dark']) html,
:host([data-theme-mode='dark']) body {
  background: #262626;
}

/* Prose body text. */
:host([data-theme-mode='dark']) .md-typeset {
  color: var(--helix-colors-neutral-200);
}

:host([data-theme-mode='dark']) .md-typeset p,
:host([data-theme-mode='dark']) .md-typeset ul,
:host([data-theme-mode='dark']) .md-typeset ol {
  color: var(--helix-colors-neutral-200);
}

/* Headings read a step brighter than the body. */
:host([data-theme-mode='dark']) .md-typeset h1,
:host([data-theme-mode='dark']) .md-typeset h2,
:host([data-theme-mode='dark']) .md-typeset h3,
:host([data-theme-mode='dark']) .md-typeset h4,
:host([data-theme-mode='dark']) .md-typeset h5,
:host([data-theme-mode='dark']) .md-typeset h6 {
  color: var(--helix-colors-neutral-100);
}

/* Links keep the light-mode blue; restated for clarity. */
:host([data-theme-mode='dark']) .md-typeset a {
  color: var(--helix-colors-accent-blue-500);
}

:host([data-theme-mode='dark']) .md-typeset a:hover {
  color: var(--helix-colors-accent-blue-600);
}

/*------------------  Doc nav  ------------------*/

/* Section labels and the doc-set title read bright; leaf rows read dim. */
:host([data-theme-mode='dark']) .md-nav--primary > .md-nav__title,
:host([data-theme-mode='dark']) .md-nav__item--nested > .md-nav__link {
  color: var(--helix-colors-neutral-100);
}

:host([data-theme-mode='dark']) .md-nav__link {
  color: #a0a0a0; /* secondary label — no Helix dark token yet; reconcile. */
}

:host([data-theme-mode='dark']) .md-nav__link:hover {
  background: var(--helix-colors-transparent-white-10);
  color: var(--helix-colors-neutral-100);
}

/* Active page: yellow-tint pill and bright label. */
:host([data-theme-mode='dark'])
  .md-nav__item--active:not(.md-nav__item--nested)
  > .md-nav__link,
:host([data-theme-mode='dark']) .md-nav .md-nav__link--active {
  background: color-mix(in srgb, var(--helix-colors-brand-500) 26%, transparent);
  color: var(--helix-colors-neutral-100);
}

/* The 3px active rail carries the brand yellow, as it does in light. */
:host([data-theme-mode='dark'])
  .md-nav__item--active:not(.md-nav__item--nested)
  > .md-nav__link::before,
:host([data-theme-mode='dark']) .md-nav .md-nav__link--active::before {
  background: var(--helix-colors-brand-500);
}

/* Primary sidebar divider. */
:host([data-theme-mode='dark']) .md-sidebar--primary {
  border-right-color: #3a3a3a; /* hairline — no Helix dark token yet; reconcile. */
}

/* Edit-page button: the light chip's neutral treatment on the dark surface. */
:host([data-theme-mode='dark']) .md-typeset .md-content__button {
  background: rgba(255, 255, 255, 0.06);
  border-color: #3a3a3a; /* hairline; reconcile with dark token. */
  color: var(--helix-colors-neutral-100);
}

:host([data-theme-mode='dark']) .md-typeset .md-content__button:hover {
  background: rgba(255, 255, 255, 0.11);
  border-color: #555;
  color: var(--helix-colors-neutral-100);
}

/*------------------  Prose blocks  ------------------*/

:host([data-theme-mode='dark']) .md-typeset blockquote {
  border-left-color: #3a3a3a !important; /* hairline; reconcile with dark token. */
  color: #a0a0a0; /* secondary text; reconcile with dark token. */
}

:host([data-theme-mode='dark']) .md-typeset hr {
  border-top-color: #3a3a3a; /* hairline; reconcile with dark token. */
}

/*
 * Table card: panel a tier below the canvas, a white-inset ring instead of the
 * light black ring, plus the same soft drop shadow.
 */
:host([data-theme-mode='dark']) .md-typeset table:not([class]) {
  background: #1c1c1c; /* panel tier below the canvas; reconcile with dark token. */
  box-shadow: 0 0 0 1px var(--helix-colors-transparent-white-10),
    0 2px 4px rgba(0, 0, 0, 0.04);
}

:host([data-theme-mode='dark']) .md-typeset table:not([class]) th {
  border-bottom-color: #3a3a3a; /* hairline; reconcile with dark token. */
  color: var(--helix-colors-neutral-100);
}

:host([data-theme-mode='dark']) .md-typeset table:not([class]) td {
  border-top-color: #3a3a3a; /* hairline; reconcile with dark token. */
  color: var(--helix-colors-neutral-200);
}

/* Inline code chip: a tier above the canvas; text stays currentColor. */
:host([data-theme-mode='dark']) .md-typeset code {
  background: #333333; /* chip tier above the canvas; reconcile. */
}

/*------------------  Code blocks  ------------------*/

/*
 * Light mode keeps the plain ink surface from the base rules above, matching
 * the design's light reader. On the dark canvas that same surface no longer
 * separates, so the block drops a tier darker and gains a hairline white ring
 * to read as a distinct inset. The token palette is unchanged — it is already
 * a light-on-ink set that carries across both modes.
 */
:host([data-theme-mode='dark']) .md-typeset .highlight,
:host([data-theme-mode='dark']) .md-typeset pre {
  background: #0f0f0f; /* darkest code surface; no Helix token yet */
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14);
}

/* The inner surfaces share the wrapper's darker tier; the ring belongs to the
 * outer box alone, so drop the one the broad \`pre\` rule above would repeat. */
:host([data-theme-mode='dark']) .md-typeset .highlight pre,
:host([data-theme-mode='dark']) .md-typeset .highlight > pre,
:host([data-theme-mode='dark']) .md-typeset pre > code {
  background: #0f0f0f;
  box-shadow: none;
}

:host([data-theme-mode='dark']) .md-typeset .highlighttable .linenos,
:host([data-theme-mode='dark']) .md-typeset .highlight .linenos,
:host([data-theme-mode='dark']) .md-typeset .highlight .lineno {
  background: #0f0f0f;
}

/* The copy button's focus ring fakes a gap with the surface colour. */
:host([data-theme-mode='dark']) .hone-code-copy:focus-visible {
  box-shadow: 0 0 0 2px #0f0f0f, 0 0 0 4px #039cfb;
}

/*------------------  Callouts  ------------------*/

/*
 * Neutral default (note / abstract / example / quote): a panel a tier below the
 * canvas with a white-inset ring. The tinted types below keep their light rgba
 * tints — they composite fine over the dark canvas — but must be restated so
 * this neutral surface, equal in specificity and later in source order, does
 * not clobber them.
 */
:host([data-theme-mode='dark']) .md-typeset .admonition,
:host([data-theme-mode='dark']) .md-typeset details {
  background: #1c1c1c; /* neutral callout surface; reconcile with dark token. */
  box-shadow: inset 0 0 0 1px var(--helix-colors-transparent-white-10);
  color: var(--helix-colors-neutral-200);
}

:host([data-theme-mode='dark']) .md-typeset .admonition-title,
:host([data-theme-mode='dark']) .md-typeset summary {
  color: var(--helix-colors-neutral-100);
}

/* The icon is a mask filled by background-color; the light dark-ink fill would
 * go invisible on the dark surface, so the neutral icon flips to a bright one. */
:host([data-theme-mode='dark']) .md-typeset .admonition-title::before,
:host([data-theme-mode='dark']) .md-typeset summary::before {
  background-color: var(--helix-colors-neutral-100) !important;
}

/* Info / note-blue. */
:host([data-theme-mode='dark']) .md-typeset .admonition.info,
:host([data-theme-mode='dark']) .md-typeset .admonition.todo,
:host([data-theme-mode='dark']) .md-typeset .admonition.question,
:host([data-theme-mode='dark']) .md-typeset .admonition.help,
:host([data-theme-mode='dark']) .md-typeset .admonition.faq,
:host([data-theme-mode='dark']) .md-typeset details.info,
:host([data-theme-mode='dark']) .md-typeset details.question {
  background: rgba(3, 156, 251, 0.06);
  box-shadow: inset 0 0 0 1px rgba(3, 156, 251, 0.3);
}

:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.info
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.todo
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.question
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.help
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.faq
  > .admonition-title,
:host([data-theme-mode='dark']) .md-typeset details.info > summary,
:host([data-theme-mode='dark']) .md-typeset details.question > summary {
  color: var(--helix-colors-accent-blue-500);
}

:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.info
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.todo
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.question
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.help
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.faq
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  details.info
  > summary::before,
:host([data-theme-mode='dark'])
  .md-typeset
  details.question
  > summary::before {
  background-color: var(--helix-colors-accent-blue-500) !important;
}

/*
 * Tip is the only opaque pale-yellow surface in light; on the dark canvas that
 * glares, so it joins the other tints as a translucent brand-yellow wash.
 */
:host([data-theme-mode='dark']) .md-typeset .admonition.tip,
:host([data-theme-mode='dark']) .md-typeset .admonition.hint,
:host([data-theme-mode='dark']) .md-typeset .admonition.important,
:host([data-theme-mode='dark']) .md-typeset details.tip {
  background: color-mix(in srgb, var(--helix-colors-brand-500) 8%, transparent);
  box-shadow: inset 0 0 0 1px
    color-mix(in srgb, var(--helix-colors-brand-500) 30%, transparent);
}

:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.tip
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.hint
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.important
  > .admonition-title,
:host([data-theme-mode='dark']) .md-typeset details.tip > summary {
  color: var(--helix-colors-brand-500);
}

:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.tip
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.hint
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.important
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  details.tip
  > summary::before {
  background-color: var(--helix-colors-brand-500) !important;
}

/* Warning / caution. */
:host([data-theme-mode='dark']) .md-typeset .admonition.warning,
:host([data-theme-mode='dark']) .md-typeset .admonition.caution,
:host([data-theme-mode='dark']) .md-typeset .admonition.attention,
:host([data-theme-mode='dark']) .md-typeset details.warning {
  background: rgba(194, 97, 10, 0.06);
  box-shadow: inset 0 0 0 1px rgba(194, 97, 10, 0.3);
}

:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.warning
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.caution
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.attention
  > .admonition-title,
:host([data-theme-mode='dark']) .md-typeset details.warning > summary {
  color: #e8963f; /* lightened warning hue for dark; reconcile with dark token. */
}

:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.warning
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.caution
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.attention
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  details.warning
  > summary::before {
  background-color: #e8963f !important; /* lightened warning hue; reconcile. */
}

/* Success / check / done. */
:host([data-theme-mode='dark']) .md-typeset .admonition.success,
:host([data-theme-mode='dark']) .md-typeset .admonition.check,
:host([data-theme-mode='dark']) .md-typeset .admonition.done,
:host([data-theme-mode='dark']) .md-typeset details.success {
  background: rgba(24, 154, 92, 0.06);
  box-shadow: inset 0 0 0 1px rgba(24, 154, 92, 0.3);
}

:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.success
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.check
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.done
  > .admonition-title,
:host([data-theme-mode='dark']) .md-typeset details.success > summary {
  color: #3ecf8e; /* lightened success hue for dark; reconcile with dark token. */
}

:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.success
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.check
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.done
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  details.success
  > summary::before {
  background-color: #3ecf8e !important; /* lightened success hue; reconcile. */
}

/* Danger / error / failure / bug. */
:host([data-theme-mode='dark']) .md-typeset .admonition.danger,
:host([data-theme-mode='dark']) .md-typeset .admonition.error,
:host([data-theme-mode='dark']) .md-typeset .admonition.failure,
:host([data-theme-mode='dark']) .md-typeset .admonition.fail,
:host([data-theme-mode='dark']) .md-typeset .admonition.missing,
:host([data-theme-mode='dark']) .md-typeset .admonition.bug,
:host([data-theme-mode='dark']) .md-typeset details.danger,
:host([data-theme-mode='dark']) .md-typeset details.bug {
  background: rgba(217, 48, 54, 0.06);
  box-shadow: inset 0 0 0 1px rgba(217, 48, 54, 0.3);
}

:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.danger
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.error
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.failure
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.fail
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.missing
  > .admonition-title,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.bug
  > .admonition-title,
:host([data-theme-mode='dark']) .md-typeset details.danger > summary,
:host([data-theme-mode='dark']) .md-typeset details.bug > summary {
  color: #ff5a5f; /* lightened error hue for dark; reconcile with dark token. */
}

:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.danger
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.error
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.failure
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.fail
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.missing
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  .admonition.bug
  > .admonition-title::before,
:host([data-theme-mode='dark'])
  .md-typeset
  details.danger
  > summary::before,
:host([data-theme-mode='dark'])
  .md-typeset
  details.bug
  > summary::before {
  background-color: #ff5a5f !important; /* lightened error hue; reconcile. */
}
`;
